import { useRef, useEffect } from 'react';

// Shared storage for scroll positions across all modal instances
const scrollPositions = new Map<string, { top: number; left: number }>();

/**
 * Hook to preserve scroll position of a scrollable container
 * Each modal maintains its own scroll position independently
 *
 *
 * @example
 * const scrollRef = usePreserveScroll(modalId);
 * <div ref={scrollRef} className="overflow-auto">
 *   {content}
 * </div>
 */
export const usePreserveScroll = (modalId: string) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Store scroll position whenever it changes (via scroll event)
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleScroll = () => {
      scrollPositions.set(modalId, {
        top: element.scrollTop,
        left: element.scrollLeft,
      });
    };

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [modalId]);

  // Restore scroll position on every render
  useEffect(() => {
    if (!scrollRef.current) return;

    const savedPosition = scrollPositions.get(modalId);
    if (savedPosition) {
      // Force restore with requestAnimationFrame
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = savedPosition.top;
          scrollRef.current.scrollLeft = savedPosition.left;
        }
      });
    }
  });

  return scrollRef;
};
